import { FunnelId } from 'constants/funnel';
import { IMGS_PATH } from 'constants/image';
import clsx from 'clsx';
import Image from 'components/ui/Image';
import Link from 'components/ui/Link';
import Stars from 'components/ui/Stars';
import { useAnalytics } from 'hooks/useAnalytics';
import { useTranslation } from 'hooks/useTranslation';
import { replaceSpecialChar } from 'utils/string';
import Button from 'components/Qds/Button';
import ExternalFunnelButton from 'components/Shared/ExternalFunnel/ExternalFunnelButton';
import type { ClinicPage } from 'types/Clinic';

type Props = {
  fields: ClinicPage;
  className?: string;
};

const ClinicSliderItem = ({ fields, className }: Props): JSX.Element => {
  const { t, locale } = useTranslation();
  const { trackEvent } = useAnalytics();

  const { name, photos: photosCollection } = fields;
  const photos = photosCollection.items || [];

  return (
    <div className={clsx('mx-1 md:mx-3 rounded-lg', className)}>
      <div className="border border-silver-light rounded">
        <div className="overflow-hidden relative">
          <div className="full-lazyload w-full h-56 relative overflow-hidden">
            <Image
              src={photos[0]?.url || `${IMGS_PATH.PUBLIC}/clinic-default.svg`}
              className="w-full h-full object-cover"
              alt={
                photos[0]?.description ||
                photos[0]?.title ||
                replaceSpecialChar(name)
              }
              fill
              contentful
            />
          </div>
          <div className="p-3">
            <div>
              <p className="font-bold text-navy h-12">
                {replaceSpecialChar(name)}
              </p>
            </div>

            <div className="flex items-center md:mb-5 h-6">
              {fields.reviews?.[0] && (
                <>
                  <Stars rating={fields.reviews[0].avg_rating as number} />{' '}
                  <span className="ml-2 text-sm">
                    {fields.reviews[0].total} {t('reviews')}
                  </span>
                </>
              )}
            </div>

            {fields.slug && (
              <Link href={`/${locale}/${fields.slug}`}>
                <a className="stretched-link" title={name} />
              </Link>
            )}

            <div className="font-netto my-3 z-20 relative">
              <ExternalFunnelButton
                funnelId={FunnelId.CONTACT_FORM}
                renderButton={(openFunnel) => {
                  return (
                    <Button
                      title={t('getAQuote')}
                      fullWidth
                      onClick={() => {
                        trackEvent({
                          category: 'CTA',
                          action: 'CTA|Click',
                          label: `CTA|GEO|ClinicCard|GetaQuote`,
                        });

                        openFunnel();
                      }}
                    >
                      {t('getAQuote')}
                    </Button>
                  );
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicSliderItem;
